import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useContractor } from 'context/ContractorContext';
import {
    getWorkplaces,
    getContractorProfile,
    clockIn,
    clockOut,
    addBreak,
    getTimecards,
} from 'services/contractorPortal';

const formatDuration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}hr ${m}m ${s}s`;
};

const ClockTime = () => {
    const { contractorId } = useContractor();
    const queryClient = useQueryClient();
    const [selectedWorkplace, setSelectedWorkplace] = useState('');
    const [elapsed, setElapsed] = useState(0);
    const [error, setError] = useState('');
    const [activeTimecard, setActiveTimecard] = useState(null);
    const [now, setNow] = useState(new Date());

    // Live clock
    useEffect(() => {
        const tick = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(tick);
    }, []);

    const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

    const { data: workplaces = [] } = useQuery({ queryKey: ['cp-workplaces'], queryFn: getWorkplaces });

    const { data: contractor } = useQuery({
        queryKey: ['cp-contractor', contractorId],
        queryFn: () => getContractorProfile(contractorId),
        enabled: !!contractorId,
    });

    const { data: rawTimecards = [] } = useQuery({
        queryKey: ['cp-timecards', contractorId],
        queryFn: () => getTimecards(contractorId),
        enabled: !!contractorId,
    });

    // Filter to only this contractor's timecards (backend may return all company timecards)
    const timecards = rawTimecards.filter(t =>
        t.contractorId === contractorId || (!t.employeeId && !t.contractorId)
    );

    useEffect(() => {
        const open = timecards.find(t => t.status === 'open');
        if (open) setActiveTimecard(open);
    }, [timecards]);

    // Auto-select workplace from contractor profile (primaryWorkplaceId takes priority)
    useEffect(() => {
        if (!selectedWorkplace && contractor) {
            const wpId = contractor.primaryWorkplaceId || contractor.workplaceId;
            if (wpId) setSelectedWorkplace(wpId);
        }
    }, [contractor, selectedWorkplace]);

    // Live elapsed timer
    useEffect(() => {
        let timer;
        if (activeTimecard?.clockInTime) {
            const start = new Date(activeTimecard.clockInTime).getTime();
            timer = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
        } else {
            setElapsed(0);
        }
        return () => clearInterval(timer);
    }, [activeTimecard]);

    const { mutate: doClockIn, isPending: clockingIn } = useMutation({
        mutationFn: () => clockIn(contractorId, selectedWorkplace || null),
        onSuccess: (data) => { setActiveTimecard(data); setError(''); queryClient.invalidateQueries(['cp-timecards', contractorId]); },
        onError: (err) => setError(err?.response?.data?.message || 'Clock-in failed.'),
    });

    const { mutate: doClockOut, isPending: clockingOut } = useMutation({
        mutationFn: () => clockOut(activeTimecard.id),
        onSuccess: () => { setActiveTimecard(null); setElapsed(0); setError(''); queryClient.invalidateQueries(['cp-timecards', contractorId]); },
        onError: (err) => setError(err?.response?.data?.message || 'Clock-out failed.'),
    });

    const { mutate: doBreak } = useMutation({
        mutationFn: ({ paid }) => addBreak(activeTimecard.id, paid, 1800),
        onSuccess: (data) => { setActiveTimecard(data); queryClient.invalidateQueries(['cp-timecards', contractorId]); },
    });

    const isClockedIn = !!activeTimecard;
    const workplace = workplaces.find(w => (w.uuid || w.id) === selectedWorkplace);
    const workplaceName = workplace?.name || '—';
    const workplaceTimezone = workplace?.timezone || workplace?.filing_address?.state || contractor?.state || 'N/A';
    const workplaceAddress = workplace?.address || workplace?.filing_address?.street_1
        || [contractor?.addressLine1, contractor?.city, contractor?.state].filter(Boolean).join(', ') || 'N/A';

    const contractorFullName = contractor ? `${contractor.firstName || ''} ${contractor.lastName || ''}`.trim() : '—';
    const clockInDisplay  = activeTimecard?.clockInTime  ? new Date(activeTimecard.clockInTime).toLocaleTimeString()  : '—';
    const clockOutDisplay = activeTimecard?.clockOutTime ? new Date(activeTimecard.clockOutTime).toLocaleTimeString() : '—';

    const infoCell = (label, value) => (
        <div>
            <p style={{ color: '#6c757d', fontSize: 11, marginBottom: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</p>
            <p style={{ fontWeight: 600, fontSize: 14, margin: 0, color: '#222' }}>{value || 'N/A'}</p>
        </div>
    );

    const ActionBtn = ({ label, onClick, active, disabled }) => (
        <button onClick={onClick} disabled={disabled} style={{
            background: active ? '#E7730A' : '#f0f0f0',
            color: active ? '#fff' : '#bbb',
            border: 'none', borderRadius: 8,
            padding: '13px 0', fontWeight: 700,
            fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer',
            letterSpacing: 1, width: '100%', transition: 'background 0.2s',
        }}>
            {label}
        </button>
    );

    return (
        <div style={{ padding: '24px', backgroundColor: '#F4F5F7', minHeight: '100vh' }}>

            {/* Clock Header */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ fontSize: 32, fontWeight: 700, color: '#E7730A', letterSpacing: 2, fontVariantNumeric: 'tabular-nums' }}>
                    {timeStr}
                </div>
                <div style={{ fontSize: 16, color: '#555', fontWeight: 500 }}>{dateStr}</div>
            </div>

            {/* Status banner */}
            {isClockedIn && (
                <div style={{ background: '#d4edda', color: '#155724', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 14, textAlign: 'center', fontWeight: 600 }}>
                    ✓ Clocked in since {clockInDisplay} — {formatDuration(elapsed)} elapsed
                </div>
            )}
            {error && (
                <div style={{ background: '#f8d7da', color: '#721c24', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 14 }}>
                    {error}
                </div>
            )}

            {/* Info Cards Row */}
            <div className="row g-3 mb-3">

                {/* Workplace Card */}
                <div className="col-md-6">
                    <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', height: '100%' }}>
                        <p style={{ fontWeight: 700, fontSize: 13, color: '#E7730A', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Workplace</p>

                        {(contractor?.primaryWorkplaceId || contractor?.workplaceId) ? (
                            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '10px 14px', fontWeight: 600, fontSize: 15, color: '#333', marginBottom: 14, border: '1px solid #e9ecef' }}>
                                {workplaceName}
                            </div>
                        ) : (
                            <select className="form-select mb-3" value={selectedWorkplace} onChange={e => setSelectedWorkplace(e.target.value)} disabled={isClockedIn}>
                                <option value="">— Select Workplace —</option>
                                {workplaces.map(w => <option key={w.uuid || w.id} value={w.uuid || w.id}>{w.name}</option>)}
                            </select>
                        )}

                        <div className="row g-2">
                            <div className="col-6">{infoCell('Timezone', workplaceTimezone)}</div>
                            <div className="col-6">{infoCell('Address', workplaceAddress)}</div>
                        </div>
                    </div>
                </div>

                {/* Contractor Card */}
                <div className="col-md-6">
                    <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', height: '100%' }}>
                        <p style={{ fontWeight: 700, fontSize: 13, color: '#E7730A', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Contractor</p>

                        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '10px 14px', fontWeight: 600, fontSize: 15, color: '#333', marginBottom: 14, border: '1px solid #e9ecef' }}>
                            {contractorFullName}
                        </div>

                        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                            <span style={{ fontSize: 12, color: '#1a9fbb', fontWeight: 600 }}>Contractor ID</span>
                            <span style={{ background: '#e9ecef', borderRadius: 6, padding: '2px 10px', fontSize: 12, color: '#555', wordBreak: 'break-all' }}>
                                {contractorId || 'N/A'}
                            </span>
                        </div>

                        <div className="row g-2">
                            <div className="col-4">{infoCell('First Name', contractor?.firstName)}</div>
                            <div className="col-4">{infoCell('Last Name', contractor?.lastName)}</div>
                            <div className="col-4">{infoCell('Start Date', contractor?.startDate ? new Date(contractor.startDate).toLocaleDateString() : null)}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Time Stats + Action Buttons Row — only for Hourly */}
            {(contractor?.payType || '').toLowerCase() === 'hourly' ? (
                <div className="row g-3 mb-3">

                    {/* Stats */}
                    <div className="col-md-6">
                        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', height: '100%' }}>
                            <p style={{ fontWeight: 700, fontSize: 13, color: '#E7730A', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>Time Tracking</p>
                            <div className="row g-3 mb-3">
                                <div className="col-6">{infoCell('Shift Hours', formatDuration(elapsed))}</div>
                                <div className="col-6">{infoCell('Paid Hours', formatDuration(Math.max(0, elapsed - (activeTimecard?.unpaidBreakSeconds || 0))))}</div>
                                <div className="col-6">{infoCell('Paid Breaks', formatDuration(activeTimecard?.paidBreakSeconds || 0))}</div>
                                <div className="col-6">{infoCell('Unpaid Breaks', formatDuration(activeTimecard?.unpaidBreakSeconds || 0))}</div>
                            </div>
                            <div className="row g-2">
                                <div className="col-6">{infoCell('Clock-In Time', clockInDisplay)}</div>
                                <div className="col-6">{infoCell('Clock-Out Time', clockOutDisplay)}</div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons — Clock-In / Clock-Out only */}
                    <div className="col-md-6">
                        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', height: '100%' }}>
                            <p style={{ fontWeight: 700, fontSize: 13, color: '#E7730A', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>Actions</p>
                            <div className="row g-2">
                                <div className="col-6">
                                    <ActionBtn label={clockingIn ? 'CLOCKING IN...' : 'CLOCK-IN'} onClick={doClockIn} active={!isClockedIn} disabled={isClockedIn || clockingIn || !contractorId} />
                                </div>
                                <div className="col-6">
                                    <ActionBtn label={clockingOut ? 'CLOCKING OUT...' : 'CLOCK-OUT'} onClick={doClockOut} active={isClockedIn} disabled={!isClockedIn || clockingOut} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', textAlign: 'center', color: '#6c757d', fontSize: 14 }}>
                    Clock In / Clock Out is only available for <strong>Hourly</strong> contractors.
                    Your pay type is <strong style={{ textTransform: 'capitalize' }}>{contractor?.payType || '—'}</strong> — your payment is processed automatically each pay period.
                </div>
            )}

            {/* Recent Timecards — only for Hourly contractors */}
            {(contractor?.payType || '').toLowerCase() === 'hourly' && timecards.length > 0 && (
                <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.07)' }}>
                    <p style={{ fontWeight: 700, fontSize: 13, color: '#E7730A', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>Recent Timecards</p>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                                {['Date', 'Clock In', 'Clock Out', 'Total Hours', 'Status'].map(h => (
                                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#555', fontWeight: 600, fontSize: 12 }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {timecards.slice(0, 5).map(t => (
                                <tr key={t.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '10px 12px' }}>{t.clockInTime ? new Date(t.clockInTime).toLocaleDateString() : '—'}</td>
                                    <td style={{ padding: '10px 12px' }}>{t.clockInTime ? new Date(t.clockInTime).toLocaleTimeString() : '—'}</td>
                                    <td style={{ padding: '10px 12px' }}>{t.clockOutTime ? new Date(t.clockOutTime).toLocaleTimeString() : '—'}</td>
                                    <td style={{ padding: '10px 12px' }}>{t.totalHours ? `${t.totalHours} hrs` : '—'}</td>
                                    <td style={{ padding: '10px 12px' }}>
                                        <span style={{
                                            padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500,
                                            background: t.status === 'approved' ? '#d4edda' : t.status === 'completed' ? '#fff3cd' : '#cce5ff',
                                            color: t.status === 'approved' ? '#155724' : t.status === 'completed' ? '#856404' : '#004085',
                                        }}>
                                            {t.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ClockTime;
